import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthContext } from '@/App';
import { Sparkles, Upload, LogOut, Crown, FileSpreadsheet, Eye, Share2, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, logout, setUser } = useContext(AuthContext);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [fileId, setFileId] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadDashboards();
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user data');
    }
  };

  const loadDashboards = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboards(response.data);
    } catch (error) {
      toast.error('Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const extension = selectedFile.name.split('.').pop().toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(extension)) {
        toast.error('Please upload a CSV or Excel file');
        return;
      }
      setFile(selectedFile);
      setFileId(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/dashboard/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFileId(response.data.file_id);
      toast.success(`File uploaded: ${response.data.row_count} rows, ${response.data.columns.length} columns`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerate = async () => {
    if (!fileId) {
      toast.error('Please upload a file first');
      return;
    }

    if (!user.is_premium && user.free_usage_count >= 3) {
      toast.error('Free usage limit reached!');
      navigate('/pricing');
      return;
    }

    setGenerating(true);

    try {
      const response = await axios.post(`${API}/dashboard/generate`, 
        { file_id: fileId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Dashboard generated successfully!');
      setFile(null);
      setFileId(null);
      loadDashboards();
      loadUserData();
      navigate(`/dashboard/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Generation failed');
      if (error.response?.status === 403) {
        navigate('/pricing');
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass border-b border-white/5 h-20 flex items-center justify-between px-8 sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <Sparkles className="h-8 w-8 text-primary" />
          <span className="text-2xl font-bold gradient-text">DashAI</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground" data-testid="usage-count">
            {user.is_premium ? (
              <span className="flex items-center gap-2 text-primary font-medium">
                <Crown className="h-4 w-4" /> Premium
              </span>
            ) : (
              <span>{user.free_usage_count} / 3 free dashboards used</span>
            )}
          </div>
          {!user.is_premium && (
            <Link to="/pricing">
              <Button variant="outline" size="sm" data-testid="upgrade-btn" className="rounded-full">
                <Crown className="h-4 w-4 mr-2" /> Upgrade
              </Button>
            </Link>
          )}
          <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-8 py-12 max-w-7xl">
        {/* Upload Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-2xl p-8 mb-12"
        >
          <h2 className="text-3xl font-bold mb-6">Create New Dashboard</h2>
          
          <div className="space-y-6">
            <div>
              <label htmlFor="file-upload" className="block mb-2 text-sm font-medium text-muted-foreground">
                Upload CSV or Excel File
              </label>
              <div className="flex gap-4">
                <Input
                  id="file-upload"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  data-testid="file-upload-input"
                  className="flex-1 bg-zinc-900/50 border-white/10 h-12"
                />
                <Button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  data-testid="upload-btn"
                  className="rounded-full px-6"
                >
                  {uploading ? 'Uploading...' : (
                    <><Upload className="h-4 w-4 mr-2" /> Upload</>
                  )}
                </Button>
              </div>
              {file && (
                <p className="text-sm text-muted-foreground mt-2" data-testid="file-name">
                  Selected: {file.name}
                </p>
              )}
            </div>

            {fileId && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <p className="text-sm font-medium text-primary mb-2">File uploaded successfully!</p>
                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  data-testid="generate-btn"
                  className="rounded-full px-8 font-semibold"
                  size="lg"
                >
                  {generating ? 'Generating with AI...' : (
                    <><Sparkles className="h-5 w-5 mr-2" /> Generate Dashboard with AI</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Dashboards List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-3xl font-bold mb-6">Your Dashboards</h2>
          
          {loading ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="loading-dashboards">
              Loading dashboards...
            </div>
          ) : dashboards.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center" data-testid="no-dashboards">
              <FileSpreadsheet className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">No dashboards yet. Upload a file to get started!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dashboards.map((dashboard) => (
                <motion.div
                  key={dashboard.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-zinc-950/50 backdrop-blur-md border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all card-hover"
                  data-testid={`dashboard-card-${dashboard.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-1 truncate">{dashboard.file_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {new Date(dashboard.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <FileSpreadsheet className="h-8 w-8 text-primary" />
                  </div>
                  
                  <div className="flex gap-2">
                    <Link to={`/dashboard/${dashboard.id}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full" data-testid={`view-btn-${dashboard.id}`}>
                        <Eye className="h-4 w-4 mr-2" /> View
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const shareUrl = `${window.location.origin}/share/${dashboard.share_token}`;
                        navigator.clipboard.writeText(shareUrl);
                        toast.success('Share link copied!');
                      }}
                      data-testid={`share-btn-${dashboard.id}`}
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;